import { useFlag } from '@unleash/proxy-client-react';

// generate this list by exporting feature flags as json from unleash
// copy the json, and paste it in this snippet: (<json goes here>).features.map(({ name }) => name)
// and just copy the resulting array
const FeatureFlagNames = [
  'accessToFormativeAssessment',
  'access_to_new_novelstudies',
  'add-material-file',
  'aiDrawingFeedback',
  'ai-templates',
  'AI_TEMPLATES',
  'AI_TEMPLATES_CONSUME',
  'browse_by_standards',
  'CLASSLINK_NEW_UNIQUE_ID',
  'CL_PAGE_V2',
  'CL_SLIDES_PREVIEW',
  'CURILIVE_FORCE_RELOAD',
  'CURIPOD_OUTTAKES',
  'custom-trial-expiration-dates',
  'discover',
  'discover_admin',
  'dynamic_curify',
  'dynamic_full_lesson',
  'dynamic_lesson_prompt_updates',
  'dynamic_lesson_v2',
  'enableNewCreatePage',
  'FALL_24_CONTENT_HIERARCHY',
  'GENERATOR_PAGE_RECOMMENDATIONS',
  'HOTJAR_MONITORING',
  'isAllowedToPublishGenerator',
  'isNewIAEnabled',
  'isTranslateTabEnabled',
  'IS_PRIVATE_SETTINGS',
  'IS_UNDER_MAINTENANCE',
  'isUseAttentionLock',
  'l10n-experiment',
  'localtest',
  'objective-refinement-on-landing',
  'orgPickerV2',
  'orgPickerV2districtSignupEnabled',
  'REVERSE_TRIAL_MVP',
  'rubric-based-ai-feedback',
  'SCHOOL_USAGE',
  'ADMIN_SCORE_INSIGHTS',
  'gaps-admin-dashboard',
  'INTERNAL_ADMIN_DASHBOARD',
  'IN_PRODUCT_BACKOFFICE',
  'showMaintananceToast',
  'single_testprep_form',
  'STUDENT_FLEXIBILITY',
  'templated_customizable_lessons',
  'tenor_gifs',
  'useAiFeedbackPrompt',
  'useAzureOpenAi',
  'useCompareAndContrastLessonFlag',
  'useCountrySchoolSelectionV2',
  'useCurimoteNavigation',
  'useGeneratorPlugin',
  'use_new_dynamic_lesson_blocks',
  'useNewParticipantsLogic',
  'useNewReportPrompt',
  'useNewResults',
  'useOpenAIIsDown',
  'usePromptResultsTesting',
  'USE_RECOMMENDED_GENERATORS',
  'useTestingGeneratorsFlag',
  'show_individual_premium_unauthenticated',
  'international_individual_premium',
  'show_sample_lessons',
  'newCuriQuizFlow',
  'display_state_picker_home_internal',
  'activity-revision-editing',
  'vocabulary_dropdown',
  'auth-errors-troubleshoot',
  'lesson-limit-system-v2',
  'can_impersonate',
  'pre_made_math_lessons',
  'spelling_practice',
  'phonics_wizard',
  'LATIN_ROOTS_WIZARD',
  'SOCIAL_STUDIES_ROOTS_WIZARD',
  'SCIENCE_ROOTS_WIZARD',
  'grammar_practice',
  'drag_timer',
  'TESTPREP_RUBRIC_EDITING',
  'premium-testprep-play-limit',
  // DB standards for GA, SC, NC, OH, NJ, MI, LA, NV, WA, IA, CO, TN, NY, OK, MS — create in Unleash dashboard
  'use_db_standards_more_states',
  'internal_save_generated_lessons_as_free',
  'http_detect_strengths',
  'force_open_great_picks',
  'native_retry',
  'ai_feedback_score',
  'multiple_choice',
  'internal_copy_lesson_data',
  'as-many-as-you-can-competition',
  'org_members_can_edit_any_lesson',
  // Replaces the small "add a copy of this lesson" dialog on /edit with the
  // full-screen lesson preview (slides + copy/present CTAs). Merges OFF; flip
  // on in Unleash when ready.
  'lesson_preview_full_screen',
  // Dark-launch gate for the TX-only "How does Curipod fit my Curriculum?"
  // banner + curriculum-fit subpage on the writing home. Merges OFF; flip on
  // in Unleash when ready.
  'curriculum_fit_banner',
  // Lesson preview modal on the curriculum-fit subpage: clicking a sample
  // lesson card opens the in-page slide preview (present/edit CTAs) instead of
  // navigating straight to /edit. Merges OFF; flip on in Unleash for testing.
  'curriculum_fit_lesson_preview',
  // Per-curriculum access on the curriculum-fit subpage. When on, that
  // curriculum's lessons are fully unlocked (no locks / paywall) and not-yet-
  // built units show "Coming soon" instead of the paywall.
  // Split by grade band: a district buys one vendor's programme, not the
  // vendor. The unsuffixed flags cover grades 3-5 (HMH Into Reading, Savvas
  // myView); the _6_8 flags cover the separate secondary programmes (Into
  // Literature, myPerspectives) that share the same dropdown row.
  'curriculum_access_bluebonnet',
  'curriculum_access_hmh',
  'curriculum_access_hmh_6_8',
  'curriculum_access_savvas',
  'curriculum_access_savvas_6_8',
  'math_word_problems',
  'STUDENT_SSO',
  'SSO_SELF_SERVE',
  // Internal debug switch, already in Unleash and read directly via `useFlag`
  // in the flexible-lesson levels. Also gates the agent's tool calls in the
  // refinement chat ("Looked up standards", "Read your file", …) — insight
  // into what the agent did, for us rather than for teachers.
  'flexible_lesson_debug',
  // Dark-launch gate for "Curipod skill plans" on the curriculum-fit page — the
  // goal-ordered third index (see skillPlansData.ts) alongside the curriculum
  // browsers and Browse by TEKS. Gates the dropdown row, the view, and the
  // deep link, so with it off the page behaves exactly as before. Sits inside
  // curriculum_fit_banner, which still gates the whole subpage. Merges OFF;
  // create in Unleash.
  'curriculum_skill_plans',
] as const;

type FeatureFlagName = typeof FeatureFlagNames[number];

export function useFeatureFlag(name: FeatureFlagName): boolean {
  const isEnabled = useFlag(name);
  return isEnabled;
}
